// ============================================================================
// BRAND ASSETS – Centralized logo & image imports
// ============================================================================
// Logos are resolved **dynamically** from the active theme preset's `assets`
// section (src/config/themes/*.json).  All files in the assets/ folder are
// eagerly imported via import.meta.glob so any filename referenced by a theme
// JSON is automatically resolved to a proper Vite asset URL.
//
// Usage:
//   import { LogoSplash, LogoMain, BackgroundMain } from "@/config/brandAssets";
//   <img src={LogoSplash} alt="Logo" />
// ============================================================================

import { assets } from "./theme";

// ---------------------------------------------------------------------------
// Build a filename → URL map from every file in assets/
// ---------------------------------------------------------------------------
const assetModules = import.meta.glob<string>(
  "../../assets/*",
  { eager: true, query: "?url", import: "default" },
);

const assetByFilename: Record<string, string> = {};
for (const [path, url] of Object.entries(assetModules)) {
  const filename = path.split("/").pop() ?? "";
  assetByFilename[filename] = url;
}

// ---------------------------------------------------------------------------
// Helper: resolve a theme-JSON filename to a Vite asset URL
// ---------------------------------------------------------------------------
function resolveAsset(filename: string): string {
  return assetByFilename[filename] ?? "";
}

// ---------------------------------------------------------------------------
// Exported assets (all consumers import from here)
// ---------------------------------------------------------------------------
export const LogoMain: string = resolveAsset(assets.logo.main);
export const LogoSplash: string = resolveAsset(assets.logo.splash);
export const BackgroundMain: string = resolveAsset(assets.backgrounds.main);
export const CodingCloudLight: string = resolveAsset(assets.partner.light);
export const CodingCloudDark: string = resolveAsset(assets.partner.dark);

export const getBrandLogo = (variant: "main" | "splash" = "splash"): string => {
  return variant === "main" ? LogoMain : LogoSplash;
};

export const getCodingCloudLogo = (isDarkMode: boolean): string => {
  return isDarkMode ? CodingCloudDark : CodingCloudLight;
};

// Logotipo horizontal para el encabezado, según el fondo: "light" va sobre
// fondo claro (letras oscuras) y "dark" sobre fondo oscuro. Cadena vacía si
// el tema no lo define o el archivo no está en assets/: el encabezado vuelve
// entonces al ícono de la app + título.
export const getWordmark = (isDarkMode: boolean): string => {
  const wordmark = assets.logo.wordmark;
  if (!wordmark) return "";
  return resolveAsset(isDarkMode ? wordmark.dark : wordmark.light);
};
